/**
 * Custom hook for LaunchDarkly feature flag evaluation
 *
 * Uses LaunchDarkly client directly to evaluate flags without triggering re-renders.
 * This approach prevents unnecessary re-renders when unrelated flags change.
 */

import { useState, useEffect, useMemo } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  FeatureFlagKey,
  getEffectiveDefault
} from './featureFlagConfig';
import { useLaunchDarklyState } from './LaunchDarklyProvider';

/**
 * Convert kebab-case to camelCase
 * LaunchDarkly React SDK converts flag keys to camelCase by default
 */
const toCamelCase = (str: string): string => {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Hook to get a feature flag value
 *
 * @param flagKey - The feature flag key to evaluate
 * @param defaultValue - Optional override for the default value
 * @returns The boolean flag value
 *
 * @example
 * const isEnhancedLoggingEnabled = useFeatureFlag(FeatureFlags.UI_ENHANCED_LOGGING_ENABLED);
 * if (isEnhancedLoggingEnabled) {
 *   // Enhanced logging is enabled
 * }
 */
export const useFeatureFlag = (
  flagKey: FeatureFlagKey,
  defaultValue?: boolean
): boolean => {
  // Read initialization state from context
  const { isEnabled, isInitialized } = useLaunchDarklyState();
  const ldClient = useLDClient();

  // Memoize effective default to prevent unnecessary re-renders
  const effectiveDefault = useMemo(
    () => getEffectiveDefault(flagKey, defaultValue),
    [flagKey, defaultValue]
  );

  // LD SDK converts kebab-case to camelCase
  const camelCaseKey = useMemo(() => toCamelCase(flagKey), [flagKey]);

  // Local state for the flag value
  const [flagValue, setFlagValue] = useState<boolean>(effectiveDefault);

  useEffect(() => {
    // When LD is not enabled or not initialized, use the effective default
    if (!isEnabled || !isInitialized || !ldClient) {
      setFlagValue(effectiveDefault);
      return;
    }

    // Get initial value from client
    // IMPORTANT: variation() expects the canonical kebab-case key, not camelCase
    const currentValue = ldClient.variation(flagKey, effectiveDefault);
    setFlagValue(typeof currentValue === 'boolean' ? currentValue : effectiveDefault);

    // Subscribe to changes for THIS flag only
    const changeListener = (current: unknown) => {
      setFlagValue(typeof current === 'boolean' ? current : effectiveDefault);
    };

    // Listen for general 'change' event and check if our flag changed
    const generalChangeListener = (changes: Record<string, { current: unknown; previous: unknown }>) => {
      // Check both camelCase and kebab-case keys
      if (changes[camelCaseKey]) {
        const newValue = changes[camelCaseKey].current;
        setFlagValue(typeof newValue === 'boolean' ? newValue : effectiveDefault);
      } else if (changes[flagKey]) {
        const newValue = changes[flagKey].current;
        setFlagValue(typeof newValue === 'boolean' ? newValue : effectiveDefault);
      }
    };

    // Try both event formats: specific flag change and general change
    ldClient.on(`change:${camelCaseKey}`, changeListener); // Specific flag - camelCase
    ldClient.on(`change:${flagKey}`, changeListener); // Specific flag - kebab-case
    ldClient.on('change', generalChangeListener); // General change event

    // Cleanup listeners on unmount
    return () => {
      ldClient.off(`change:${camelCaseKey}`, changeListener);
      ldClient.off(`change:${flagKey}`, changeListener);
      ldClient.off('change', generalChangeListener);
    };
  }, [isEnabled, isInitialized, ldClient, camelCaseKey, effectiveDefault, flagKey]);

  return flagValue;
};
